import * as React from 'react';
import { createOverflowManager } from '@fluentui/priority-overflow';
import { canUseDOM, useEventCallback, useIsomorphicLayoutEffect } from '@fluentui/react-utilities';
import { DATA_OVERFLOWING, DATA_OVERFLOW_DIVIDER, DATA_OVERFLOW_ITEM, DATA_OVERFLOW_MENU } from './constants';
/**
 * @internal
 * @param update - Callback when overflow state changes
 * @param options - Options to configure the overflow container
 * @returns - ref to attach to an intrinsic HTML element and imperative functions
 */ export const useOverflowContainer = (update, options)=>{
    const { overflowAxis, overflowDirection, padding, minimumVisible, onUpdateItemVisibility } = options;
    // DOM ref to the overflow container element
    const containerRef = React.useRef(null);
    const updateOverflowItems = useEventCallback(update);
    const [overflowManager] = React.useState(()=>canUseDOM() ? createOverflowManager() : null);
    useIsomorphicLayoutEffect(()=>{
        if (!containerRef.current) {
            return;
        }
        if (overflowManager) {
            overflowManager.observe(containerRef.current, {
                overflowDirection: overflowDirection !== null && overflowDirection !== void 0 ? overflowDirection : 'end',
                overflowAxis: overflowAxis !== null && overflowAxis !== void 0 ? overflowAxis : 'horizontal',
                padding: padding !== null && padding !== void 0 ? padding : 10,
                minimumVisible: minimumVisible !== null && minimumVisible !== void 0 ? minimumVisible : 0,
                onUpdateItemVisibility: onUpdateItemVisibility !== null && onUpdateItemVisibility !== void 0 ? onUpdateItemVisibility : ()=>undefined,
                onUpdateOverflow: updateOverflowItems !== null && updateOverflowItems !== void 0 ? updateOverflowItems : ()=>undefined
            });
            return ()=>{
                overflowManager.disconnect();
            };
        }
    }, [
        updateOverflowItems,
        overflowManager,
        overflowDirection,
        overflowAxis,
        padding,
        minimumVisible,
        onUpdateItemVisibility
    ]);
    const registerItem = React.useCallback((item)=>{
        overflowManager === null || overflowManager === void 0 ? void 0 : overflowManager.addItem(item);
        item.element.setAttribute(DATA_OVERFLOW_ITEM, '');
        return ()=>{
            item.element.removeAttribute(DATA_OVERFLOWING);
            item.element.removeAttribute(DATA_OVERFLOW_ITEM);
            overflowManager === null || overflowManager === void 0 ? void 0 : overflowManager.removeItem(item.id);
        };
    }, [
        overflowManager
    ]);
    const registerDivider = React.useCallback((divider)=>{
        const el = divider.element;
        overflowManager === null || overflowManager === void 0 ? void 0 : overflowManager.addDivider(divider);
        el && el.setAttribute(DATA_OVERFLOW_DIVIDER, '');
        return ()=>{
            divider.groupId && (overflowManager === null || overflowManager === void 0 ? void 0 : overflowManager.removeDivider(divider.groupId));
            el.removeAttribute(DATA_OVERFLOW_DIVIDER);
        };
    }, [
        overflowManager
    ]);
    const updateOverflow = React.useCallback(()=>{
        overflowManager === null || overflowManager === void 0 ? void 0 : overflowManager.update();
    }, [
        overflowManager
    ]);
    const registerOverflowMenu = React.useCallback((el)=>{
        overflowManager === null || overflowManager === void 0 ? void 0 : overflowManager.addOverflowMenu(el);
        el.setAttribute(DATA_OVERFLOW_MENU, '');
        return ()=>{
            overflowManager === null || overflowManager === void 0 ? void 0 : overflowManager.removeOverflowMenu();
            el.removeAttribute(DATA_OVERFLOW_MENU);
        };
    }, [
        overflowManager
    ]);
    return {
        containerRef,
        registerItem,
        updateOverflow,
        registerOverflowMenu,
        registerDivider
    };
};
export const updateVisibilityAttribute = ({ item, visible })=>{
    if (visible) {
        item.element.removeAttribute(DATA_OVERFLOWING);
    } else {
        item.element.setAttribute(DATA_OVERFLOWING, '');
    }
};
